import React from 'react';
import { DateInput } from '../DateInput';
import { VOUCHER_ORDER } from './constants';
import { useStore } from '../../store/useStore';

interface VoucherHeaderProps {
  voucherType: string;
  isAlterMode: boolean;
  derivedVoucherConfig: Record<string, any>;
  referenceNo: string;
  setReferenceNo: (v: string) => void;
  erpVoucherNumber: string;
  voucherDate: string;
  setVoucherDate: (v: string) => void;
  focusedField: string;
  focusField: (id: string) => void;
  firstField: (vt: string) => string;
  handleVoucherSwitch: (vt: string) => void;
  formData: any;
  ledgerDb: Record<string, any>;
  handleInputChange: (fieldId: string, value: string) => void;
  handleKeyDown: (e: React.KeyboardEvent, fieldId: string) => void;
  inputRefs: React.MutableRefObject<Record<string, any>>;
  handleRefKeyDown: (e: React.KeyboardEvent) => void;
}

export const VoucherHeader: React.FC<VoucherHeaderProps> = ({
  voucherType, isAlterMode, derivedVoucherConfig, referenceNo, setReferenceNo,
  erpVoucherNumber, voucherDate, setVoucherDate, focusedField, focusField, firstField,
  handleVoucherSwitch, formData, ledgerDb, handleInputChange, handleKeyDown, inputRefs,
  handleRefKeyDown,
}) => {
  const cfg = derivedVoucherConfig[voucherType];
  const partyGroup   = formData.party.value ? ledgerDb[formData.party.value]?.group   : null;
  const partyBalance = formData.party.value ? ledgerDb[formData.party.value]?.balance : null;

  const fmtDisplayDate = (d: string) => {
    if (!d) return '';
    const p = d.split('-');
    return p.length === 3 ? `${parseInt(p[2])}-${parseInt(p[1])}-${p[0]}` : d;
  };

  return (
    <>
      {/* Voucher type switcher & Header Settings */}
      <div className="flex items-center gap-2 border-b border-[#aebec8] px-2 py-[4px] bg-[#d9e3eb]">
        <div className="bg-[#1e8585] text-white px-4 py-[2px] text-[13px] font-bold self-center min-w-[100px] text-center rounded-sm">
          {voucherType}
        </div>

        <div className="flex items-center ml-2">
          <span className="text-black text-[12px] font-semibold mr-2 hidden sm:inline-block">Change Voucher:</span>
          <select
            value={voucherType}
            onChange={(e) => { if (!isAlterMode) handleVoucherSwitch(e.target.value); }}
            disabled={isAlterMode}
            title={isAlterMode ? 'Voucher type cannot be changed in alteration mode' : ''}
            className={`bg-white text-[#1e8585] border border-[#aebec8] rounded px-2 py-[2px] text-[12px] font-semibold outline-none ${isAlterMode ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-[#d9e3eb]'}`}
            tabIndex={-1}
          >
            {VOUCHER_ORDER.map(type => (
              <option key={type} value={type}>
                {type} ({derivedVoucherConfig[type]?.key || ''})
              </option>
            ))}
          </select>
        </div>

        <div className="ml-auto flex items-center gap-4 pr-1 text-[12px] self-center">
          {/* Date first */}
          {focusedField === 'voucherDate' ? (
            <DateInput
              value={voucherDate}
              onChange={(newDate: string) => {
                if (newDate) {
                  setVoucherDate(newDate);
                  useStore.getState().setSystemDate(newDate);
                }
              }}
              onConfirm={() => focusField('referenceNo')}
              autoFocus
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Escape') {
                  e.stopPropagation();
                  focusField('referenceNo');
                }
              }}
              className="w-[90px] font-bold text-right outline-none px-1 border border-[#1e8585] box-border max-w-full min-w-0 bg-[#eefcfc] text-black"
            />
          ) : (
            <input
              type="text"
              value={fmtDisplayDate(voucherDate)}
              readOnly
              onClick={() => focusField('voucherDate')}
              className="w-[90px] font-bold text-right outline-none px-1 border border-transparent box-border max-w-full min-w-0 bg-transparent text-black cursor-pointer"
              spellCheck={false}
            />
          )}

          {/* Ref second */}
          <div className="flex items-center gap-3 mr-2">
            <div className="flex items-center">
              <span className="text-black text-[11px] mr-1">Ref:</span>
              <input
                ref={(el: any) => { inputRefs.current['referenceNo'] = el; }}
                type="text"
                value={referenceNo}
                onChange={e => setReferenceNo(e.target.value)}
                onFocus={() => focusField('referenceNo')}
                onKeyDown={handleRefKeyDown}
                placeholder="Manual No"
                className={`w-[80px] font-bold outline-none px-1 py-[1px] border text-[11px] text-black placeholder-gray-400 placeholder-opacity-70 ${focusedField === 'referenceNo' ? 'bg-[#eefcfc] border-[#1e8585]' : 'bg-white border-[#aebec8]'}`}
              />
            </div>
            {/* Vch No. last — readonly/auto */}
            <div className="flex items-center">
              <span className="text-black text-[11px]">Vch No.</span>
              <strong className="text-black ml-1 min-w-[30px] inline-block text-[12px]">{erpVoucherNumber || (isAlterMode ? '—' : 'Auto')}</strong>
            </div>
          </div>
        </div>
      </div>

      {/* Party / Sales / Purchase header */}
      {cfg?.useParty && (
        <div className="px-2 pt-2 pb-1 bg-[#eef6ea]">
          <div className={`flex items-center mb-[2px] ${partyGroup ? '' : 'invisible'}`}>
            <div className="w-[120px] text-[13px]">Ledger Group</div>
            <div className="w-[12px] text-center">:</div>
            <div className="font-bold text-[13px] px-1 text-[#1e8585]">{partyGroup || ' '}</div>
          </div>
          <div className="flex items-center mb-[2px]">
            <div className="w-[120px] text-[13px]">Party A/c name</div>
            <div className="w-[12px] text-center">:</div>
            <div className="w-[300px]">
              <input
                ref={(el: any) => { inputRefs.current['party'] = el; }}
                type="text"
                value={focusedField === 'party' ? formData.party.text : formData.party.value}
                onChange={e => handleInputChange('party', e.target.value)}
                onFocus={() => focusField('party')}
                onKeyDown={e => handleKeyDown(e as any, 'party')}
                className={`w-full max-w-full box-border min-w-0 outline-none text-[13px] py-[1px] px-1 font-semibold border border-transparent ${focusedField === 'party' ? 'bg-[#eefcfc]' : 'bg-transparent'}`}
                spellCheck={false} autoComplete="off"
              />
            </div>
          </div>
          <div className={`flex items-center mb-[2px] opacity-70 italic ${partyBalance ? '' : 'invisible'}`}>
            <div className="w-[120px] text-[12px]">Current balance</div>
            <div className="w-[12px] text-center text-[12px]">:</div>
            <div className="text-[12px] px-1 font-semibold">{partyBalance || ' '}</div>
          </div>
          {['Sales', 'Credit Note'].includes(voucherType) && (
            <div className="flex items-center mt-2 mb-[2px]">
              <div className="w-[120px] text-[13px]">Sales Ledger</div>
              <div className="w-[12px] text-center">:</div>
              <div className="w-[300px]">
                <input
                  ref={(el: any) => { inputRefs.current['sales'] = el; }}
                  type="text"
                  value={focusedField === 'sales' ? formData.sales.text : formData.sales.value}
                  onChange={e => handleInputChange('sales', e.target.value)}
                  onFocus={() => focusField('sales')}
                  onKeyDown={e => handleKeyDown(e as any, 'sales')}
                  className={`w-full max-w-full box-border min-w-0 outline-none text-[13px] py-[1px] px-1 font-semibold border border-transparent ${focusedField === 'sales' ? 'bg-[#eefcfc]' : 'bg-transparent'}`}
                  spellCheck={false} autoComplete="off"
                />
              </div>
            </div>
          )}
          {['Purchase', 'Debit Note'].includes(voucherType) && (
            <div className="flex items-center mt-2 mb-[2px]">
              <div className="w-[120px] text-[13px]">Purchase Ledger</div>
              <div className="w-[12px] text-center">:</div>
              <div className="w-[300px]">
                <input
                  ref={(el: any) => { inputRefs.current['purchase'] = el; }}
                  type="text"
                  value={focusedField === 'purchase' ? formData.purchase.text : formData.purchase.value}
                  onChange={e => handleInputChange('purchase', e.target.value)}
                  onFocus={() => focusField('purchase')}
                  onKeyDown={e => handleKeyDown(e as any, 'purchase')}
                  className={`w-full max-w-full box-border min-w-0 outline-none text-[13px] py-[1px] px-1 font-semibold border border-transparent ${focusedField === 'purchase' ? 'bg-[#eefcfc]' : 'bg-transparent'}`}
                  spellCheck={false} autoComplete="off"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Grid Headers */}
      {cfg?.useJournalGrid && (
        <div className="flex bg-[#d9e3eb] text-black font-bold text-[13px] border-b border-t border-[#aebec8] w-full box-border mt-2">
          <div className="w-[50px] min-w-[50px] max-w-[50px] shrink-0 px-2 py-[2px] border-r border-[#aebec8] text-center box-border">Dr/Cr</div>
          <div className="flex-1 min-w-0 px-2 py-[2px] border-r border-[#aebec8] box-border">Particulars</div>
          <div className="w-[130px] min-w-[130px] max-w-[130px] shrink-0 px-2 py-[2px] border-r border-[#aebec8] text-right box-border">Debit</div>
          <div className="w-[130px] min-w-[130px] max-w-[130px] shrink-0 px-2 py-[2px] text-right box-border">Credit</div>
        </div>
      )}
      {cfg?.useItemGrid && (
        <div className="flex bg-[#d9e3eb] text-black font-bold text-[13px] border-b border-t border-[#aebec8] w-full box-border mt-2">
          <div className="flex-1 min-w-0 px-2 py-[2px] border-r border-[#aebec8] box-border">Particulars</div>
          <div className="w-[80px] min-w-[80px] max-w-[80px] shrink-0 px-2 py-[2px] border-r border-[#aebec8] text-right box-border">Qty</div>
          <div className="w-[100px] min-w-[100px] max-w-[100px] shrink-0 px-2 py-[2px] border-r border-[#aebec8] text-right box-border">Rate</div>
          <div className="w-[65px] min-w-[65px] max-w-[65px] shrink-0 px-2 py-[2px] border-r border-[#aebec8] text-right box-border">Disc%</div>
          <div className="w-[160px] min-w-[160px] max-w-[160px] shrink-0 px-2 py-[2px] text-right box-border">Amount</div>
        </div>
      )}
    </>
  );
};
